import { useMemo } from "react";
import { ResponsiveLine, Serie } from "@nivo/line";
import { LegendConfig, getUniqueYearsFromSeries } from "./chartUtils";
import CustomLegend, { LegendElement } from "./CustomLegend";
import "./UsageLineChart.scss";

type UsageLineChartProps = {
  data: Serie[];
  colors: string[];
  legendConfigs: LegendConfig[];
  chartBottomMargin: number;
  tickRotation?: number;
  tickValues?: number[];
  isInteractive?: boolean;
  height?: number | string;
  showLegend?: boolean;
  showGridX?: boolean;
  showGridY?: boolean;
  yAxisLegend?: string;
  yTickValues?: number[];
};

const UsageLineChart = ({
  data,
  colors,
  legendConfigs,
  chartBottomMargin,
  tickRotation = 0,
  tickValues,
  isInteractive = true,
  height = "100%",
  showLegend = true,
  showGridX = true,
  showGridY = true,
  yAxisLegend = "DADP",
  yTickValues,
}: UsageLineChartProps) => {
  const hasSeriesData = data.some((entry) => entry.data.length > 0);
  const rawTickValues = tickValues ?? getUniqueYearsFromSeries(data);
  const integerTickValues = rawTickValues.filter((value) =>
    Number.isInteger(value)
  );
  const xTickValues =
    integerTickValues.length > 0 ? integerTickValues : undefined;

  const maxDataValue = useMemo(() => {
    let max = 0;
    data.forEach((serie) => {
      serie.data.forEach((d) => {
        const val = Number(d.y);
        if (!isNaN(val) && val > max) {
          max = val;
        }
      });
    });
    return max;
  }, [data]);

  const zeroes = Math.floor(Math.log10(Math.max(maxDataValue, 10)));
  const cappedZeroes = Math.min(zeroes, 5);
  const dynamicLegendOffset = -40 - cappedZeroes * 6.5;
  const dynamicLeftMargin = Math.abs(dynamicLegendOffset) + 20;

  if (!hasSeriesData) {
    return null;
  }

  const legendData: LegendElement[] = legendConfigs.flatMap((config) =>
    config.data.map((datum) => ({
      label: datum.label,
      color: datum.color,
    }))
  );
  const shouldRenderLegend = showLegend && legendData.length > 0;
  const effectiveBottomMargin = shouldRenderLegend
    ? chartBottomMargin
    : Math.min(chartBottomMargin, 80);

  return (
    <div className="usageLineChart" style={{ width: "100%", height }}>
      <div className="usageLineChart__chartArea">
        <ResponsiveLine
          data={data}
          margin={{ top: 24, right: 24, bottom: effectiveBottomMargin, left: dynamicLeftMargin }}
          xScale={{ type: "linear", min: "auto", max: "auto" }}
          yScale={{ type: "linear", min: 0, max: "auto", stacked: false }}
          animate={false}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 6,
            tickPadding: 8,
            legend: "Year",
            legendOffset: 48,
            legendPosition: "middle",
            format: (value) => Number(value).toString(),
            tickRotation,
            tickValues: xTickValues,
          }}
          axisLeft={{
            tickSize: 6,
            tickPadding: 8,
            legend: yAxisLegend,
            legendOffset: dynamicLegendOffset,
            legendPosition: "middle",
            tickValues: yTickValues,
            format: (value) => {
              const num = Number(value);
              return Math.abs(num) >= 100000 ? num.toExponential(1) : num.toLocaleString();
            },
          }}
          pointSize={6}
          pointBorderWidth={2}
          pointBorderColor={{ from: "serieColor" }}
          useMesh
          enableSlices="x"
          enableGridX={showGridX}
          enableGridY={showGridY}
          colors={colors}
          isInteractive={isInteractive}
        />
      </div>
      {shouldRenderLegend && (
        <div className="usageLineChart__legend">
          <CustomLegend legendData={legendData} />
        </div>
      )}
    </div>
  );
};

export default UsageLineChart;
